import { getCapabilityMapping } from '../gphSkillRegistry';
import {
  preparePubgMobileCalculatorSkill,
  executePubgMobileCalculatorSkill
} from '../adapters/pubgMobileCalculatorSkillAdapter';
import {
  prepareSensiIaSkill,
  executeSensiIaSkill
} from '../adapters/sensiIaSkillAdapter';
import {
  prepareCoachTrainingSkill,
  executeCoachTrainingSkill
} from '../adapters/coachTrainingSkillAdapter';
import {
  prepareMiAnalysisSkill,
  executeMiAnalysisSkill
} from '../adapters/miAnalysisSkillAdapter';
import {
  prepareFreefireCalculatorSkill,
  executeFreefireCalculatorSkill
} from '../adapters/freefireCalculatorSkillAdapter';
import { shouldHandlePubgCalculatorSkillText } from '../slotFilling/pubgCalculatorSlotExtractor';
import { resolveMiSkillIntentWithLLM } from './miSkillIntentResolverClient';
import {
  composeMiSkillAnswer,
  sanitizeMiUserFacingText
} from './miAnswerComposer';

export const MI_SKILL_IDS = Object.freeze({
  PUBG_MOBILE_CALCULATOR: 'pubg_mobile_calculator',
  SENSI_IA: 'sensi_ia',
  COACH_TRAINING: 'coach_training',
  MI_ANALYSIS: 'mi_analysis',
  FREEFIRE_CALCULATOR: 'freefire_calculator'
});

const getPubgCapabilityId = () => (
  getCapabilityMapping(MI_SKILL_IDS.PUBG_MOBILE_CALCULATOR)?.primaryCapabilityId || 'calculator_pubg'
);

const getSensiCapabilityId = () => (
  getCapabilityMapping(MI_SKILL_IDS.SENSI_IA)?.primaryCapabilityId || 'sensitivity_diagnostic'
);

const getCoachCapabilityId = () => (
  getCapabilityMapping(MI_SKILL_IDS.COACH_TRAINING)?.primaryCapabilityId || 'training_single_drill'
);

const getMiAnalysisCapabilityId = () => (
  getCapabilityMapping(MI_SKILL_IDS.MI_ANALYSIS)?.primaryCapabilityId || 'mi_analysis_gameplay'
);

const getFreefireCapabilityId = () => (
  getCapabilityMapping(MI_SKILL_IDS.FREEFIRE_CALCULATOR)?.primaryCapabilityId || 'calculator_freefire'
);

const MIN_CONFIDENCE_TO_USE_SKILL = 0.55;

const clearPendingContext = (extra = {}) => ({
  pendingSkill: null,
  pendingSlots: [],
  slots: {},
  ...extra
});

const keepPendingContext = (outcome = {}) => ({
  pendingSkill: MI_SKILL_IDS.PUBG_MOBILE_CALCULATOR,
  pendingSlots: outcome.missingSlots || [],
  slots: outcome.slots || {}
});

const keepSensiPendingContext = (outcome = {}) => ({
  pendingSkill: MI_SKILL_IDS.SENSI_IA,
  pendingSlots: outcome.missingSlots || [],
  slots: outcome.slots || {}
});

const keepCoachPendingContext = (outcome = {}) => ({
  pendingSkill: MI_SKILL_IDS.COACH_TRAINING,
  pendingSlots: outcome.missingSlots || [],
  slots: outcome.slots || {}
});

const keepMiAnalysisPendingContext = (outcome = {}) => {
  const safeSlots = { ...(outcome.slots || {}) };
  if (safeSlots.video) safeSlots.video = null;
  return {
    pendingSkill: MI_SKILL_IDS.MI_ANALYSIS,
    pendingSlots: outcome.missingSlots || [],
    slots: safeSlots
  };
};

const keepFreefirePendingContext = (outcome = {}) => ({
  pendingSkill: MI_SKILL_IDS.FREEFIRE_CALCULATOR,
  pendingSlots: outcome.missingSlots || [],
  slots: outcome.slots || {}
});

export const formatPubgCalculatorResultText = (result = {}) => {
  const rows = Array.isArray(result.rows) ? result.rows : [];
  const rowsText = rows
    .slice(0, 8)
    .map((row) => `${row.label}: ${row.value}`)
    .join('\n');

  return [
    result.title || 'Sensibilidade PUBG calculada',
    result.summary || null,
    rowsText || null,
    result.nextStep || null
  ].filter(Boolean).join('\n\n');
};

const isRealVideoFile = (value) => (
  (typeof File !== 'undefined' && value instanceof File)
  || (typeof Blob !== 'undefined' && value instanceof Blob)
);

const normalizeConversationText = (value = '') => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .trim();

const PREVIOUS_RESULT_REFERENCE_PATTERN = /\b(esse|essa|esses|essas|isso|resultado|tabela|valores|anterior|ultima|ultimo|última|último)\b/;
const NEW_OBJECTIVE_PATTERN = /\b(quero|preciso|me ajuda|me ajuda|monta|gera|faz|manda|recomenda|configuracao|configuração|sensibilidade|sensi)\b[\s\S]{0,120}\b(perfeita|completa|base inicial|inicial|setup|configuracao|configuração|celular|aparelho|s23|samsung|iphone|redmi|poco|xiaomi|dpi|fps|full|dedo)\b/;

const looksLikePreviousResultReference = (normalizedText = '') => (
  PREVIOUS_RESULT_REFERENCE_PATTERN.test(normalizedText)
);

const looksLikeNewObjective = (normalizedText = '') => (
  NEW_OBJECTIVE_PATTERN.test(normalizedText)
);

const sensitivityTypeLabelOf = (sensitivityType) => {
  if (sensitivityType === 'ADS') return 'ADS no dedo';
  if (sensitivityType === 'Gyro') return 'Giroscopio';
  if (sensitivityType === 'ADS Gyro') return 'ADS Gyro';
  if (sensitivityType === 'Camera') return 'Camera';
  return sensitivityType || 'sensibilidade';
};

const buildPubgLastSkillResult = (outcome = {}, capabilityId = getPubgCapabilityId()) => {
  const slots = outcome.slots || {};
  const metadata = outcome.result?.metadata || {};
  return {
    skillId: MI_SKILL_IDS.PUBG_MOBILE_CALCULATOR,
    capabilityId,
    resultKind: 'calculator_result',
    game: 'pubg_mobile',
    weapon: metadata.weapon || slots.weapon || null,
    weaponLabel: metadata.weaponLabel || slots.weaponLabel || slots.weapon || null,
    baseScope: metadata.baseScope || slots.baseScope || null,
    baseScopeLabel: metadata.baseScopeLabel || slots.baseScopeLabel || slots.baseScope || null,
    baseValue: metadata.baseValue ?? slots.baseValue ?? null,
    sensitivityType: metadata.sensitivityType || slots.sensitivityType || null,
    sensitivityTypeLabel: metadata.sensitivityTypeLabel || sensitivityTypeLabelOf(slots.sensitivityType),
    controlMode: metadata.controlMode || slots.controlMode || outcome.payload?.controlMode || null,
    title: outcome.result?.title || 'Sensibilidade PUBG calculada',
    summary: outcome.result?.summary || null,
    rows: Array.isArray(outcome.result?.rows) ? outcome.result.rows.slice(0, 12) : [],
    at: new Date().toISOString()
  };
};

const buildPubgAggregateLastSkillResult = (successes = [], failures = [], capabilityId = getPubgCapabilityId()) => ({
  skillId: MI_SKILL_IDS.PUBG_MOBILE_CALCULATOR,
  capabilityId,
  resultKind: 'calculator_aggregate_result',
  game: 'pubg_mobile',
  title: 'Sensibilidades PUBG calculadas',
  summary: `${successes.length} calculo(s) concluido(s)${failures.length ? `, ${failures.length} item(ns) pendente(s) ou com erro` : ''}.`,
  items: successes.map(({ outcome }) => buildPubgLastSkillResult(outcome, capabilityId)),
  failures: failures.map(({ label, message, status }) => ({ label, message, status })),
  at: new Date().toISOString()
});

const isPubgCalculatorResult = (result = {}) => (
  result?.skillId === MI_SKILL_IDS.PUBG_MOBILE_CALCULATOR
  && (result?.resultKind === 'calculator_result' || result?.resultKind === 'calculator_aggregate_result')
);

const inferRequestedSensitivityMode = (normalizedText = '') => {
  if (/\bads\s*gyro\b|\bgyro\s*ads\b|\bads\s*giroscopio\b|\bgiroscopio\s*ads\b/.test(normalizedText)) return 'ADS Gyro';
  if (/\bgyro\b|\bgiroscopio\b/.test(normalizedText)) return 'Gyro';
  if (/\bads\b|\bdedo\b/.test(normalizedText)) return 'ADS';
  if (/\bcamera\b|\bcamera\b/.test(normalizedText)) return 'Camera';
  return null;
};

const asksAboutPreviousResultType = (normalizedText = '') => (
  /\b(esses|esses valores|esses resultados|isso|resultado|tabela|valores|modo|tipo)\b/.test(normalizedText)
  && /\b(de que|qual|modo|tipo|ads|gyro|giroscopio|dedo|camera)\b/.test(normalizedText)
);

const isShortSensitivityModeFollowUp = (normalizedText = '') => (
  normalizedText.length <= 48
  && /^(e\s+)?(o\s+)?(ads\s*gyro|gyro\s*ads|gyro|giroscopio|ads|camera|dedo)\??$/.test(normalizedText)
);

const looksLikeComplaintAboutPreviousResult = (normalizedText = '') => (
  /\b(burro|idiota|bosta|errado|confuso|nao entendeu|nada funciona|nao era isso|que isso)\b/.test(normalizedText)
);

const buildPubgResultTypeAnswer = (lastResult = {}) => (
  `Esse resultado anterior e de ${lastResult.sensitivityTypeLabel || sensitivityTypeLabelOf(lastResult.sensitivityType)}. Usei ${lastResult.weaponLabel || 'a arma'} com ${lastResult.baseScopeLabel || 'a mira base'} ${lastResult.baseValue ?? ''} como referencia. Nao misturei Camera, ADS, Gyro e ADS Gyro.`
);

const buildPubgAggregateResultTypeAnswer = (lastResult = {}) => {
  const items = Array.isArray(lastResult.items) ? lastResult.items : [];
  const rows = items.slice(0, 6).map((item) => (
    `- ${item.weaponLabel || item.weapon || 'Arma'}: ${item.sensitivityTypeLabel || sensitivityTypeLabelOf(item.sensitivityType)}, base ${item.baseScopeLabel || item.baseScope || 'mira'} ${item.baseValue ?? ''}`
  ));
  return [
    'Esse resultado anterior tem calculos separados por item. Nao misturei Camera, ADS, Gyro e ADS Gyro.',
    rows.length ? rows.join('\n') : null
  ].filter(Boolean).join('\n');
};

const buildPubgDifferentModeQuestion = (lastResult = {}, requestedMode) => ({
  handled: true,
  skillId: MI_SKILL_IDS.PUBG_MOBILE_CALCULATOR,
  capabilityId: lastResult.capabilityId || getPubgCapabilityId(),
  status: 'needs_slots',
  sourceStatus: 'context_follow_up',
  message: `O resultado anterior era de ${lastResult.sensitivityTypeLabel || sensitivityTypeLabelOf(lastResult.sensitivityType)}. Para calcular ${sensitivityTypeLabelOf(requestedMode)} sem misturar modos, me diga o valor base desse modo na ${lastResult.baseScopeLabel || 'mira base'}.`,
  resultText: null,
  slots: {
    game: 'pubg_mobile',
    weapon: lastResult.weapon || null,
    weaponLabel: lastResult.weaponLabel || null,
    baseScope: lastResult.baseScope || null,
    baseScopeLabel: lastResult.baseScopeLabel || null,
    sensitivityType: requestedMode
  },
  missingSlots: ['baseValue'],
  invalidSlots: [],
  backendStatus: null,
  pendingContext: {
    pendingSkill: MI_SKILL_IDS.PUBG_MOBILE_CALCULATOR,
    pendingSlots: ['baseValue'],
    slots: {
      game: 'pubg_mobile',
      weapon: lastResult.weapon || null,
      weaponLabel: lastResult.weaponLabel || null,
      baseScope: lastResult.baseScope || null,
      baseScopeLabel: lastResult.baseScopeLabel || null,
      sensitivityType: requestedMode
    },
    lastSkillResult: lastResult,
    lastAssistantQuestion: `valor base de ${sensitivityTypeLabelOf(requestedMode)}`
  },
  contextPatch: null,
  log: {
    skillChosen: MI_SKILL_IDS.PUBG_MOBILE_CALCULATOR,
    capabilityId: lastResult.capabilityId || getPubgCapabilityId(),
    schemaType: 'calculator_follow_up'
  },
  nextStepsCapabilityId: lastResult.capabilityId || getPubgCapabilityId(),
  runningMessage: null,
  mountedAdapter: null
});

const buildContextAnswerTurn = (lastResult = {}, message = '') => ({
  handled: true,
  skillId: lastResult.skillId || null,
  capabilityId: lastResult.capabilityId || null,
  status: 'success',
  sourceStatus: 'context_follow_up',
  message,
  resultText: message,
  slots: {},
  missingSlots: [],
  invalidSlots: [],
  backendStatus: null,
  pendingContext: clearPendingContext({
    lastSkillResult: lastResult,
    lastAssistantQuestion: null
  }),
  contextPatch: null,
  log: {
    skillChosen: lastResult.skillId || null,
    capabilityId: lastResult.capabilityId || null,
    schemaType: 'context_follow_up'
  },
  nextStepsCapabilityId: lastResult.capabilityId || null,
  runningMessage: null,
  mountedAdapter: null
});

const planItemsForSkill = (decision = {}, skillId) => (
  Array.isArray(decision.executionPlan)
    ? decision.executionPlan.filter((item) => item?.skillId === skillId)
    : []
);

const hasPubgExecutionPlan = (decision = {}) => (
  planItemsForSkill(decision, MI_SKILL_IDS.PUBG_MOBILE_CALCULATOR).length > 1
);

const pubgPlanItemLabel = (slots = {}) => (
  slots.weaponLabel
  || (slots.weapon === 'm762' ? 'Beryl M762' : String(slots.weapon || 'item').toUpperCase())
);

const pubgPlanScopeLabel = (slots = {}) => {
  if (slots.baseScopeLabel) return slots.baseScopeLabel;
  if (slots.baseScope === 'red_dot') return 'Red Dot';
  return slots.baseScope || 'mira base';
};

const pubgPlanTypeLabel = (slots = {}) => (
  slots.sensitivityTypeLabel || sensitivityTypeLabelOf(slots.sensitivityType)
);

const buildPubgPlanMissingMessage = (missingItems = [], commonMissing = []) => {
  const labels = missingItems
    .slice(0, 5)
    .map((item) => pubgPlanItemLabel(item.slots))
    .join(', ');
  const missing = Array.from(new Set([
    ...commonMissing,
    ...missingItems.flatMap((item) => item.missingSlots || [])
  ]));
  const parts = [];
  if (missing.includes('weapon')) parts.push('arma');
  if (missing.includes('baseScope')) parts.push('mira base');
  if (missing.includes('baseValue')) parts.push('valor numerico da base');
  if (missing.includes('sensitivityType')) parts.push('tipo: Camera, ADS, Gyro ou ADS Gyro');

  return [
    `Consigo calcular esse pedido composto${labels ? ` para ${labels}` : ''}.`,
    `Antes preciso confirmar ${parts.length ? parts.join(', ') : 'os dados que faltam'}.`,
    'Se a mesma base vale para todos, me mande em uma frase. Se cada arma tem uma base diferente, mande arma + mira + valor + tipo para cada uma.'
  ].join(' ');
};

const formatPubgAggregateResultText = ({ successes = [], failures = [] } = {}) => {
  const header = successes.length === 1
    ? 'Calculei 1 resultado de PUBG Mobile.'
    : `Calculei ${successes.length} resultados de PUBG Mobile.`;
  const blocks = successes.map(({ outcome }) => {
    const metadata = outcome.result?.metadata || {};
    const rows = Array.isArray(outcome.result?.rows) ? outcome.result.rows : [];
    const rowsText = rows
      .slice(0, 8)
      .map((row) => `${row.label}: ${row.value}`)
      .join(' | ');
    return [
      `${metadata.weaponLabel || pubgPlanItemLabel(outcome.slots)} - ${metadata.sensitivityTypeLabel || pubgPlanTypeLabel(outcome.slots)}`,
      `Base: ${metadata.baseScopeLabel || pubgPlanScopeLabel(outcome.slots)} ${metadata.baseValue ?? outcome.slots?.baseValue}.`,
      rowsText
    ].filter(Boolean).join('\n');
  });

  const failureText = failures.length
    ? [
      'Itens que eu nao calculei:',
      ...failures.map(({ label, message }) => `- ${label}: ${message}`)
    ].join('\n')
    : null;

  return [
    header,
    ...blocks,
    failureText,
    'Conferi cada item separado para nao misturar arma, base ou tipo de sensibilidade.'
  ].filter(Boolean).join('\n\n');
};

const buildPubgAggregateSkillTurn = ({
  successes = [],
  failures = [],
  decision = {},
  status = 'success',
  message = null,
  pendingContext = null
} = {}) => {
  const capabilityId = getPubgCapabilityId();
  const aggregateResult = successes.length
    ? buildPubgAggregateLastSkillResult(successes, failures, capabilityId)
    : null;
  return {
    handled: true,
    skillId: MI_SKILL_IDS.PUBG_MOBILE_CALCULATOR,
    capabilityId,
    status,
    sourceStatus: status,
    message,
    resultText: successes.length ? formatPubgAggregateResultText({ successes, failures }) : null,
    slots: decision.slots || {},
    missingSlots: pendingContext?.pendingSlots || [],
    invalidSlots: failures
      .filter((failure) => failure.status === 'invalid')
      .flatMap((failure) => failure.invalidSlots || []),
    backendStatus: null,
    pendingContext: pendingContext || clearPendingContext({
      ...(aggregateResult ? { lastSkillResult: aggregateResult } : {}),
      lastAssistantQuestion: null
    }),
    contextPatch: successes.length
      ? {
        capabilityId,
        data: {
          game: 'pubg_mobile',
          itemCount: successes.length,
          weapons: successes.map(({ outcome }) => outcome.slots?.weapon || null).filter(Boolean)
        }
      }
      : null,
    log: successes.length
      ? {
        skillChosen: MI_SKILL_IDS.PUBG_MOBILE_CALCULATOR,
        capabilityId,
        slots: { executionPlanCount: successes.length },
        schemaType: 'calculator_aggregate_result'
      }
      : null,
    nextStepsCapabilityId: capabilityId,
    runningMessage: 'Calculando miras...',
    mountedAdapter: 'PubgMobileCalculatorSkillAdapter',
    semanticDecision: decision
  };
};

const runPubgExecutionPlan = async ({
  text = '',
  decision = {},
  planItems = [],
  conversationContext = {},
  fetchImpl,
  apiBaseUrl,
  authToken,
  getAuthToken,
  requestId,
  language,
  onRunning
} = {}) => {
  const commonSlots = {
    ...(conversationContext?.pendingSkill === MI_SKILL_IDS.PUBG_MOBILE_CALCULATOR ? (conversationContext.slots || {}) : {}),
    ...(decision.slots || {})
  };
  const preparedItems = planItems.map((item, index) => {
    const slots = {
      ...commonSlots,
      ...(item.slots || {})
    };
    return {
      index,
      item,
      slots,
      prepared: preparePubgMobileCalculatorSkill({ text: '', slots })
    };
  });

  const missingItems = preparedItems.filter(({ prepared }) => prepared.status === 'needs_slots');
  if (missingItems.length) {
    const message = decisionQuestionOf(decision) || buildPubgPlanMissingMessage(missingItems.map((entry) => ({
      slots: entry.prepared.slots || entry.slots,
      missingSlots: entry.prepared.missingSlots || []
    })), decision.missingSlots || []);
    return buildPubgAggregateSkillTurn({
      decision,
      status: 'needs_slots',
      message,
      pendingContext: {
        pendingSkill: MI_SKILL_IDS.PUBG_MOBILE_CALCULATOR,
        pendingSlots: Array.from(new Set(missingItems.flatMap((entry) => entry.prepared.missingSlots || []))),
        slots: commonSlots,
        pendingExecutionPlan: {
          skillId: MI_SKILL_IDS.PUBG_MOBILE_CALCULATOR,
          items: planItems.map((item) => ({
            skillId: item.skillId,
            capabilityId: item.capabilityId || getPubgCapabilityId(),
            slots: item.slots || {},
            missingSlots: item.missingSlots || []
          })),
          responsePlan: decision.responsePlan || null
        },
        lastAssistantQuestion: message
      }
    });
  }

  const invalidItems = preparedItems.filter(({ prepared }) => prepared.status === 'invalid' || prepared.status === 'unsupported');
  const readyItems = preparedItems.filter(({ prepared }) => prepared.status === 'ready');
  const failures = invalidItems.map(({ prepared, slots }) => ({
    label: pubgPlanItemLabel(prepared.slots || slots),
    status: prepared.status,
    message: prepared.message || 'Dados invalidos para esse item.',
    invalidSlots: prepared.invalidSlots || []
  }));

  if (typeof onRunning === 'function' && readyItems.length) {
    onRunning({
      skillId: MI_SKILL_IDS.PUBG_MOBILE_CALCULATOR,
      capabilityId: getPubgCapabilityId(),
      mountedAdapter: 'PubgMobileCalculatorSkillAdapter',
      message: readyItems.length > 1 ? 'Calculando miras...' : 'Calculando mira...'
    });
  }

  const successes = [];
  for (const { index, slots } of readyItems) {
    const outcome = await executePubgMobileCalculatorSkill({
      text,
      slots,
      fetchImpl,
      apiBaseUrl,
      authToken,
      getAuthToken,
      requestId: requestId ? `${requestId}_${index + 1}` : null,
      language
    });
    if (outcome.status === 'success') {
      successes.push({ index, outcome });
    } else {
      failures.push({
        label: pubgPlanItemLabel(outcome.slots || slots),
        status: outcome.status,
        message: outcome.message || 'Nao consegui calcular esse item.',
        invalidSlots: outcome.invalidSlots || []
      });
    }
  }

  if (!successes.length) {
    return buildPubgAggregateSkillTurn({
      decision,
      status: failures.some((failure) => failure.status === 'invalid') ? 'invalid' : 'error',
      message: failures.map((failure) => `${failure.label}: ${failure.message}`).join('\n') || 'Nao consegui calcular esse pedido composto.',
      successes,
      failures
    });
  }

  return buildPubgAggregateSkillTurn({
    decision,
    status: 'success',
    message: failures.length ? 'Calculei os itens validos e separei abaixo o que nao entrou.' : null,
    successes,
    failures
  });
};

const resolveContinuityTurn = ({ text = '', conversationContext = {} } = {}) => {
  const lastResult = conversationContext?.lastSkillResult;
  if (!isPubgCalculatorResult(lastResult)) return null;

  const normalizedText = normalizeConversationText(text);
  if (!normalizedText) return null;
  if (looksLikeNewObjective(normalizedText) && !looksLikePreviousResultReference(normalizedText)) return null;

  if (lastResult.resultKind === 'calculator_aggregate_result' && asksAboutPreviousResultType(normalizedText)) {
    return buildContextAnswerTurn(lastResult, buildPubgAggregateResultTypeAnswer(lastResult));
  }

  if (asksAboutPreviousResultType(normalizedText)) {
    return buildContextAnswerTurn(lastResult, buildPubgResultTypeAnswer(lastResult));
  }

  const requestedMode = inferRequestedSensitivityMode(normalizedText);
  if (
    requestedMode
    && requestedMode !== lastResult.sensitivityType
    && (isShortSensitivityModeFollowUp(normalizedText) || looksLikePreviousResultReference(normalizedText))
  ) {
    return buildPubgDifferentModeQuestion(lastResult, requestedMode);
  }

  if (looksLikeComplaintAboutPreviousResult(normalizedText)) {
    return buildContextAnswerTurn(
      lastResult,
      `Entendi. Eu nao vou te pedir tudo de novo: o resultado anterior era ${lastResult.sensitivityTypeLabel || sensitivityTypeLabelOf(lastResult.sensitivityType)} para ${lastResult.weaponLabel || 'a arma'}, usando ${lastResult.baseScopeLabel || 'a mira base'} ${lastResult.baseValue ?? ''} como base. Se ficou ambiguo, me diga qual modo quer recalcular: Camera, ADS, Gyro ou ADS Gyro.`
    );
  }

  return null;
};

const buildPubgSkillTurn = (outcome = {}) => {
  const capabilityId = getPubgCapabilityId();
  const status = outcome.status === 'blocked_or_error' ? 'error' : outcome.status;
  const shouldKeepPending = status === 'needs_slots' || status === 'invalid';
  const lastSkillResult = status === 'success'
    ? buildPubgLastSkillResult(outcome, capabilityId)
    : null;

  return {
    handled: true,
    skillId: MI_SKILL_IDS.PUBG_MOBILE_CALCULATOR,
    capabilityId,
    status,
    sourceStatus: outcome.status,
    message: outcome.message ? sanitizeMiUserFacingText(outcome.message) : null,
    resultText: status === 'success'
      ? composeMiSkillAnswer({
        skillId: MI_SKILL_IDS.PUBG_MOBILE_CALCULATOR,
        capabilityId,
        status,
        resultText: formatPubgCalculatorResultText(outcome.result),
        slots: outcome.slots || {}
      })
      : null,
    slots: outcome.slots || {},
    missingSlots: outcome.missingSlots || [],
    invalidSlots: outcome.invalidSlots || [],
    backendStatus: outcome.backendStatus || null,
    pendingContext: shouldKeepPending
      ? keepPendingContext(outcome)
      : clearPendingContext({
        ...(lastSkillResult ? { lastSkillResult } : {}),
        lastAssistantQuestion: null
      }),
    contextPatch: status === 'success'
      ? {
        capabilityId,
        data: {
          game: 'pubg_mobile',
          weapon: lastSkillResult?.weapon || outcome.slots?.weapon || null,
          weaponLabel: lastSkillResult?.weaponLabel || outcome.slots?.weaponLabel || null,
          baseValue: outcome.slots?.baseValue || null,
          baseScope: outcome.slots?.baseScope || null,
          baseScopeLabel: lastSkillResult?.baseScopeLabel || outcome.slots?.baseScopeLabel || null,
          sensitivityType: outcome.slots?.sensitivityType || null,
          sensitivityTypeLabel: lastSkillResult?.sensitivityTypeLabel || null
        }
      }
      : null,
    log: status === 'success'
      ? {
        skillChosen: MI_SKILL_IDS.PUBG_MOBILE_CALCULATOR,
        capabilityId,
        slots: outcome.slots || {},
        schemaType: 'calculator_result'
      }
      : null,
    nextStepsCapabilityId: status === 'unsupported' ? 'calculator_codm_unsupported' : capabilityId,
    runningMessage: 'Calculando miras...',
    mountedAdapter: 'PubgMobileCalculatorSkillAdapter'
  };
};

export const formatSensiIaResultText = (result = {}) => {
  const settingsText = Array.isArray(result.settingsToChange) && result.settingsToChange.length
    ? result.settingsToChange
      .slice(0, 5)
      .map((row) => `${row.label}: ${row.value || row.reason}`)
      .join('\n')
    : null;
  const keepText = Array.isArray(result.settingsToKeep) && result.settingsToKeep.length
    ? result.settingsToKeep
      .slice(0, 3)
      .map((row) => `${row.label}: manter`)
      .join('\n')
    : null;
  const trainingText = Array.isArray(result.trainingSteps) && result.trainingSteps.length
    ? result.trainingSteps.map((step) => `- ${step}`).join('\n')
    : null;

  return [
    result.title || 'Diagnostico Sensi-IA',
    result.summary || null,
    result.primary ? `Principal leitura: ${result.primary}` : null,
    result.reason || null,
    settingsText ? `Ajustes recomendados:\n${settingsText}` : null,
    keepText ? `O que manter:\n${keepText}` : null,
    trainingText ? `Teste agora:\n${trainingText}` : null,
    result.nextStep || null
  ].filter(Boolean).join('\n\n');
};

const getSensiPrimarySymptom = (slots = {}) => {
  const report = slots.symptomReport || {};
  if (Array.isArray(report.primary_symptoms) && report.primary_symptoms[0]) return report.primary_symptoms[0];
  if (report.free_text) return report.free_text;
  return null;
};

const buildSensiSkillTurn = (outcome = {}) => {
  const capabilityId = getSensiCapabilityId();
  const status = outcome.status === 'blocked_or_error' ? 'error' : outcome.status;
  const shouldKeepPending = status === 'needs_slots' || status === 'invalid';

  return {
    handled: true,
    skillId: MI_SKILL_IDS.SENSI_IA,
    capabilityId,
    status,
    sourceStatus: outcome.status,
    message: outcome.message ? sanitizeMiUserFacingText(outcome.message) : null,
    resultText: status === 'success'
      ? composeMiSkillAnswer({
        skillId: MI_SKILL_IDS.SENSI_IA,
        capabilityId,
        status,
        resultText: formatSensiIaResultText(outcome.result),
        slots: outcome.slots || {}
      })
      : null,
    slots: outcome.slots || {},
    missingSlots: outcome.missingSlots || [],
    invalidSlots: outcome.invalidSlots || [],
    backendStatus: outcome.backendStatus || null,
    pendingContext: shouldKeepPending ? keepSensiPendingContext(outcome) : clearPendingContext(),
    contextPatch: status === 'success'
      ? {
        capabilityId,
        data: {
          game: outcome.slots?.game || null,
          symptom: getSensiPrimarySymptom(outcome.slots),
          weapon: outcome.slots?.weapon || null
        }
      }
      : null,
    log: status === 'success'
      ? {
        skillChosen: MI_SKILL_IDS.SENSI_IA,
        capabilityId,
        slots: {
          game: outcome.slots?.game || null,
          hasSymptomReport: Boolean(getSensiPrimarySymptom(outcome.slots))
        },
        schemaType: 'sensitivity_diagnostic_result'
      }
      : null,
    nextStepsCapabilityId: capabilityId,
    runningMessage: 'Diagnosticando sensibilidade...',
    mountedAdapter: 'SensiIaSkillAdapter'
  };
};

export const formatCoachTrainingResultText = (result = {}) => (
  result.renderedText || result.text || null
);

const buildCoachSkillTurn = (outcome = {}) => {
  const capabilityId = getCoachCapabilityId();
  const status = outcome.status === 'blocked_or_error' ? 'error' : outcome.status;
  const shouldKeepPending = status === 'needs_slots' || status === 'invalid';

  return {
    handled: true,
    skillId: MI_SKILL_IDS.COACH_TRAINING,
    capabilityId,
    status,
    sourceStatus: outcome.status,
    message: outcome.message ? sanitizeMiUserFacingText(outcome.message) : null,
    resultText: status === 'success'
      ? composeMiSkillAnswer({
        skillId: MI_SKILL_IDS.COACH_TRAINING,
        capabilityId,
        status,
        resultText: formatCoachTrainingResultText(outcome),
        slots: outcome.slots || {}
      })
      : null,
    slots: outcome.slots || {},
    missingSlots: outcome.missingSlots || [],
    invalidSlots: outcome.invalidSlots || [],
    backendStatus: outcome.backendStatus || null,
    pendingContext: shouldKeepPending ? keepCoachPendingContext(outcome) : clearPendingContext(),
    contextPatch: status === 'success'
      ? {
        capabilityId,
        data: {
          trainingIntent: outcome.slots?.trainingIntent || null,
          trainingId: outcome.slots?.trainingId || outcome.result?.training?.id || null,
          gameOrProfile: outcome.slots?.gameOrProfile || null
        }
      }
      : null,
    log: status === 'success'
      ? {
        skillChosen: MI_SKILL_IDS.COACH_TRAINING,
        capabilityId,
        slots: {
          trainingIntent: outcome.slots?.trainingIntent || null,
          hasTrainingId: Boolean(outcome.slots?.trainingId || outcome.result?.training?.id)
        },
        schemaType: 'training_result'
      }
      : null,
    nextStepsCapabilityId: capabilityId,
    runningMessage: 'Buscando treino da Coach...',
    mountedAdapter: 'CoachTrainingSkillAdapter'
  };
};

export const formatMiAnalysisResultText = (outcome = {}) => (
  outcome.renderedText || outcome.message || null
);

const buildMiAnalysisSkillTurn = (outcome = {}) => {
  const capabilityId = getMiAnalysisCapabilityId();
  const status = outcome.status === 'blocked_or_error' ? 'error' : outcome.status;
  const shouldKeepPending = status === 'needs_slots' || status === 'invalid';

  return {
    handled: true,
    skillId: MI_SKILL_IDS.MI_ANALYSIS,
    capabilityId,
    status,
    sourceStatus: outcome.status,
    message: outcome.message ? sanitizeMiUserFacingText(outcome.message) : null,
    resultText: status === 'success'
      ? composeMiSkillAnswer({
        skillId: MI_SKILL_IDS.MI_ANALYSIS,
        capabilityId,
        status,
        resultText: formatMiAnalysisResultText(outcome),
        slots: outcome.slots || {}
      })
      : null,
    slots: outcome.slots || {},
    missingSlots: outcome.missingSlots || [],
    invalidSlots: outcome.invalidSlots || [],
    backendStatus: outcome.backendStatus || null,
    pendingContext: shouldKeepPending ? keepMiAnalysisPendingContext(outcome) : clearPendingContext(),
    contextPatch: status === 'success'
      ? {
        capabilityId,
        data: {
          game: outcome.slots?.game || null,
          analysisGoal: outcome.slots?.analysisGoal || null
        }
      }
      : null,
    log: status === 'success'
      ? {
        skillChosen: MI_SKILL_IDS.MI_ANALYSIS,
        capabilityId,
        slots: {
          game: outcome.slots?.game || null,
          analysisGoal: outcome.slots?.analysisGoal || null,
          hasVideo: Boolean(outcome.slots?.video)
        },
        schemaType: 'gameplay_analysis_started'
      }
      : null,
    nextStepsCapabilityId: capabilityId,
    runningMessage: 'Iniciando Mi Analise...',
    mountedAdapter: 'MiAnalysisSkillAdapter'
  };
};

export const formatFreefireCalculatorResultText = (outcome = {}) => (
  outcome.renderedText || outcome.message || null
);

const buildFreefireSkillTurn = (outcome = {}) => {
  const capabilityId = getFreefireCapabilityId();
  const status = outcome.status === 'blocked_or_error' ? 'error' : outcome.status;
  const shouldKeepPending = status === 'needs_slots' || status === 'invalid';

  return {
    handled: true,
    skillId: MI_SKILL_IDS.FREEFIRE_CALCULATOR,
    capabilityId,
    status,
    sourceStatus: outcome.status,
    message: outcome.message ? sanitizeMiUserFacingText(outcome.message) : null,
    resultText: status === 'success'
      ? composeMiSkillAnswer({
        skillId: MI_SKILL_IDS.FREEFIRE_CALCULATOR,
        capabilityId,
        status,
        resultText: formatFreefireCalculatorResultText(outcome),
        slots: outcome.slots || {}
      })
      : null,
    slots: outcome.slots || {},
    missingSlots: outcome.missingSlots || [],
    invalidSlots: outcome.invalidSlots || [],
    backendStatus: outcome.backendStatus || null,
    pendingContext: shouldKeepPending ? keepFreefirePendingContext(outcome) : clearPendingContext(),
    contextPatch: status === 'success'
      ? {
        capabilityId,
        data: {
          game: 'free_fire',
          platform: outcome.slots?.platform || null,
          playStyle: outcome.slots?.playStyle || null,
          aimProblem: outcome.slots?.aimProblem || null
        }
      }
      : null,
    log: status === 'success'
      ? {
        skillChosen: MI_SKILL_IDS.FREEFIRE_CALCULATOR,
        capabilityId,
        slots: {
          platform: outcome.slots?.platform || null,
          fps: outcome.slots?.fps || null,
          hz: outcome.slots?.hz || null
        },
        schemaType: 'calculator_result'
      }
      : null,
    nextStepsCapabilityId: capabilityId,
    runningMessage: 'Calculando Free Fire...',
    mountedAdapter: 'FreefireCalculatorSkillAdapter'
  };
};

// EMERGENCY FALLBACK ONLY: heuristica legada usada exclusivamente quando o
// resolver semantico esta indisponivel (rede/LLM fora). Nunca e o caminho
// principal de decisao da Mi — a intencao vem da Mi Brain no backend.
export const shouldRouteMiSkillTurn = ({ text = '', conversationContext = {} } = {}) => (
  conversationContext?.pendingSkill === MI_SKILL_IDS.PUBG_MOBILE_CALCULATOR
  || shouldHandlePubgCalculatorSkillText(text)
);

const isSemanticResolverUnavailable = (decision = {}) => (
  decision?.source === 'frontend_fallback'
  || String(decision?.reason || '').startsWith('semantic_resolver')
  || String(decision?.reason || '').includes('resolver_')
);

// Intents da Mi Brain que apontam para execucao de skill (com coleta de slots
// quando faltarem dados) versus intents conversacionais, tratados pela camada
// de conversa com a dica semantica preservada.
const SKILL_SIDE_INTENTS = new Set(['use_skill', 'continue_pending']);
const CONVERSATIONAL_INTENTS = new Set([
  'general_help', 'vague_help', 'small_talk', 'out_of_scope', 'noise_or_abuse'
]);

// Pergunta contextual vinda da Mi Brain — tem prioridade sobre mensagens
// padrao de coleta quando o validator reporta slots faltantes.
const decisionQuestionOf = (decision = {}) => (
  decision.nextQuestion || decision.userFacingMessage || null
);

// Gate unico de skill: pendencia ativa, decisao semantica confiante, ou
// clarificacao direcionada aquela skill. Substitui as cinco copias anteriores.
const decisionTargetsSkill = (decision = {}, skillId, hasPending = false) => (
  hasPending
  || (
    SKILL_SIDE_INTENTS.has(decision.intent)
    && decision.skillId === skillId
    && decision.confidence >= MIN_CONFIDENCE_TO_USE_SKILL
  )
  || (decision.intent === 'ask_clarification' && decision.skillId === skillId)
);

// Anexa a decisao semantica ao turn e, quando o validator pede slots,
// prioriza a pergunta contextual da Mi Brain sobre a frase padrao de coleta.
// Mensagens de 'invalid' (dado errado) continuam vindo do validator, que e
// mais preciso sobre O QUE esta invalido.
const finalizeSkillTurn = (turn = {}, decision = {}) => {
  const question = decisionQuestionOf(decision);
  if (turn.status === 'needs_slots' && question) {
    return {
      ...turn,
      message: question,
      pendingContext: turn.pendingContext
        ? { ...turn.pendingContext, lastAssistantQuestion: question }
        : turn.pendingContext,
      semanticDecision: decision
    };
  }
  if (turn.status === 'needs_slots' && turn.message && turn.pendingContext) {
    return {
      ...turn,
      pendingContext: {
        ...turn.pendingContext,
        lastAssistantQuestion: turn.message
      },
      semanticDecision: decision
    };
  }
  return { ...turn, semanticDecision: decision };
};

const buildClarificationTurn = (decision = {}) => ({
  handled: true,
  skillId: decision.skillId || null,
  capabilityId: decision.capabilityId || null,
  status: 'needs_slots',
  sourceStatus: 'ask_clarification',
  message: decisionQuestionOf(decision) || 'Me da so mais um detalhe para eu escolher a ferramenta certa.',
  resultText: null,
  slots: decision.slots || {},
  missingSlots: decision.missingSlots || [],
  invalidSlots: [],
  backendStatus: null,
  pendingContext: {
    pendingSkill: decision.skillId || null,
    pendingSlots: decision.missingSlots || [],
    slots: decision.slots || {},
    lastAssistantQuestion: decisionQuestionOf(decision) || null
  },
  contextPatch: null,
  log: null,
  nextStepsCapabilityId: decision.capabilityId || null,
  runningMessage: null,
  mountedAdapter: null,
  semanticDecision: decision
});

const buildUnsupportedTurn = (decision = {}) => ({
  handled: true,
  skillId: decision.skillId || null,
  capabilityId: decision.capabilityId || 'calculator_codm_unsupported',
  status: 'unsupported',
  sourceStatus: 'unsupported',
  message: decision.userFacingMessage || 'Essa ferramenta automatica ainda nao esta disponivel. Posso te ajudar com Sensi-IA ou treino.',
  resultText: null,
  slots: decision.slots || {},
  missingSlots: [],
  invalidSlots: [],
  backendStatus: null,
  pendingContext: clearPendingContext(),
  contextPatch: null,
  log: null,
  nextStepsCapabilityId: decision.capabilityId || 'calculator_codm_unsupported',
  runningMessage: null,
  mountedAdapter: null
});

const GUIDANCE_STRATEGIES = new Set([
  'explain_limit_and_offer_baseline',
  'switch_context'
]);

const GUIDANCE_TASK_TYPES = new Set([
  'baseline_recommendation',
  'configuration_plan',
  'general_guidance',
  'refinement'
]);

const shouldAnswerWithGuidance = (decision = {}) => (
  Boolean(decision.userFacingMessage)
  && decision.requiresTool !== true
  && (
    decision.canAnswerWithGuidance === true
    || GUIDANCE_STRATEGIES.has(decision.answerStrategy)
    || (decision.answerStrategy === 'direct_answer' && GUIDANCE_TASK_TYPES.has(decision.taskType))
    || GUIDANCE_TASK_TYPES.has(decision.taskType)
  )
);

const guidanceNextStepCapability = (decision = {}) => {
  if (decision.taskType === 'baseline_recommendation' || decision.taskType === 'configuration_plan') {
    return getSensiCapabilityId();
  }
  if (decision.taskType === 'training') return getCoachCapabilityId();
  if (decision.taskType === 'analysis') return getMiAnalysisCapabilityId();
  return decision.capabilityId || null;
};

const buildGuidanceTurn = (decision = {}) => ({
  handled: true,
  skillId: decision.skillId || null,
  capabilityId: decision.capabilityId || null,
  status: 'success',
  sourceStatus: decision.answerStrategy || 'agentic_guidance',
  message: decision.userFacingMessage,
  resultText: decision.userFacingMessage,
  slots: decision.slots || {},
  missingSlots: decision.missingSlots || [],
  invalidSlots: [],
  backendStatus: null,
  pendingContext: clearPendingContext({
    lastAssistantQuestion: null,
    lastSkillResult: null,
    lastGuidance: {
      taskType: decision.taskType || null,
      answerStrategy: decision.answerStrategy || null,
      at: new Date().toISOString()
    }
  }),
  contextPatch: null,
  log: {
    skillChosen: null,
    capabilityId: decision.capabilityId || null,
    schemaType: 'agentic_guidance',
    taskType: decision.taskType || null,
    answerStrategy: decision.answerStrategy || null
  },
  nextStepsCapabilityId: guidanceNextStepCapability(decision),
  runningMessage: null,
  mountedAdapter: null,
  semanticDecision: decision
});

// Bloqueio comercial decidido pelo backend: nenhuma skill roda, a resposta é
// o convite ao Pro. O status 'locked' é tratado pelo GphAgentApp com paywall.
const buildEntitlementLockedTurn = (decision = {}) => ({
  handled: true,
  skillId: null,
  capabilityId: 'purchase_pro',
  status: 'locked',
  sourceStatus: decision.lockType || 'entitlement_locked',
  message: sanitizeMiUserFacingText(
    decision.userFacingMessage,
    'Esse recurso é exclusivo do plano Pro. Assine o Pro para liberar a Mi e todas as ferramentas.'
  ),
  resultText: null,
  slots: {},
  missingSlots: [],
  invalidSlots: [],
  backendStatus: null,
  pendingContext: clearPendingContext(),
  contextPatch: null,
  log: {
    skillChosen: null,
    capabilityId: 'purchase_pro',
    schemaType: 'entitlement_locked',
    lockType: decision.lockType || null
  },
  nextStepsCapabilityId: null,
  runningMessage: null,
  mountedAdapter: null,
  semanticDecision: decision
});

export const runMiSkillTurn = async ({
  text = '',
  conversationContext = {},
  host = {},
  fetchImpl,
  apiBaseUrl,
  authToken = null,
  getAuthToken = null,
  requestId = null,
  language = 'pt',
  semanticDecisionOverride = null,
  onRunning = null
} = {}) => {
  const continuityTurn = resolveContinuityTurn({ text, conversationContext });
  if (continuityTurn) {
    return continuityTurn;
  }

  const semanticDecision = semanticDecisionOverride || await resolveMiSkillIntentWithLLM({
    text,
    conversationContext,
    pendingSkill: conversationContext?.pendingSkill || null,
    fetchImpl,
    apiBaseUrl,
    authToken,
    getAuthToken: getAuthToken || (() => host.user?.getIdToken?.()),
    language
  });

  // Entitlement guard do backend tem prioridade absoluta: usuário bloqueado
  // não roda skill, não cai em fallback local e recebe o paywall.
  if (semanticDecision.locked) {
    return buildEntitlementLockedTurn(semanticDecision);
  }

  const hasPendingPubgSkill = conversationContext?.pendingSkill === MI_SKILL_IDS.PUBG_MOBILE_CALCULATOR;
  const hasPendingSensiSkill = conversationContext?.pendingSkill === MI_SKILL_IDS.SENSI_IA;
  const hasPendingCoachSkill = conversationContext?.pendingSkill === MI_SKILL_IDS.COACH_TRAINING;
  const hasPendingMiAnalysisSkill = conversationContext?.pendingSkill === MI_SKILL_IDS.MI_ANALYSIS;
  const hasPendingFreefireSkill = conversationContext?.pendingSkill === MI_SKILL_IDS.FREEFIRE_CALCULATOR;
  const hasAnyPendingSkill = hasPendingPubgSkill || hasPendingSensiSkill || hasPendingCoachSkill
    || hasPendingMiAnalysisSkill || hasPendingFreefireSkill;
  const resolverUnavailable = isSemanticResolverUnavailable(semanticDecision);
  const canUseFallback = resolverUnavailable && shouldRouteMiSkillTurn({ text, conversationContext });
  const canStartNewSkill = !hasAnyPendingSkill;

  // Resposta agentic: a Mi Brain decidiu que a melhor saida e orientar,
  // explicar limite e oferecer baseline/refinamento sem executar ferramenta.
  // Isso evita prender pedidos amplos ("sensi perfeita para S23") no ultimo
  // resultado da calculadora ou em slot filling estreito.
  if (!canUseFallback && shouldAnswerWithGuidance(semanticDecision)) {
    return buildGuidanceTurn(semanticDecision);
  }

  // Intents conversacionais (small_talk, vague_help, ruido, fora de escopo,
  // ajuda geral): a camada de conversa responde, mas a decisao semantica
  // viaja junto no turn — a dica da Mi Brain nunca e descartada.
  if (!canUseFallback && CONVERSATIONAL_INTENTS.has(semanticDecision.intent) && !hasAnyPendingSkill) {
    return { handled: false, semanticDecision };
  }

  // Pedido de ferramenta inexistente: resposta honesta com alternativa real,
  // nunca fallback generico e nunca promessa de skill que nao existe.
  if (!canUseFallback && semanticDecision.intent === 'unsupported') {
    return buildUnsupportedTurn(semanticDecision);
  }

  // Clarificacao sem skill definida: usa a pergunta contextual da Mi Brain.
  if (
    !canUseFallback
    && !hasAnyPendingSkill
    && semanticDecision.intent === 'ask_clarification'
    && !semanticDecision.skillId
  ) {
    if (decisionQuestionOf(semanticDecision)) {
      return buildClarificationTurn(semanticDecision);
    }
    return { handled: false, semanticDecision };
  }

  const semanticSkillId = semanticDecision.skillId || null;
  const pendingPubgPlanItems = hasPendingPubgSkill && Array.isArray(conversationContext?.pendingExecutionPlan?.items)
    ? conversationContext.pendingExecutionPlan.items
    : [];
  const semanticPubgPlanItems = planItemsForSkill(semanticDecision, MI_SKILL_IDS.PUBG_MOBILE_CALCULATOR);
  const shouldRunPubgExecutionPlan = pendingPubgPlanItems.length > 1 || (
    canStartNewSkill
    && hasPubgExecutionPlan(semanticDecision)
    && decisionTargetsSkill(semanticDecision, MI_SKILL_IDS.PUBG_MOBILE_CALCULATOR, false)
  );

  if (shouldRunPubgExecutionPlan) {
    const planItems = pendingPubgPlanItems.length > 1 ? pendingPubgPlanItems : semanticPubgPlanItems;
    const turn = await runPubgExecutionPlan({
      text,
      decision: {
        ...semanticDecision,
        responsePlan: semanticDecision.responsePlan || conversationContext?.pendingExecutionPlan?.responsePlan || null
      },
      planItems,
      conversationContext,
      fetchImpl,
      apiBaseUrl,
      authToken,
      getAuthToken: getAuthToken || (() => host.user?.getIdToken?.()),
      requestId,
      language,
      onRunning
    });
    return finalizeSkillTurn(turn, semanticDecision);
  }

  const shouldRunFreefireSkill = hasPendingFreefireSkill || (
    canStartNewSkill && decisionTargetsSkill(semanticDecision, MI_SKILL_IDS.FREEFIRE_CALCULATOR, false)
  );

  if (shouldRunFreefireSkill) {
    const existingSlots = hasPendingFreefireSkill ? (conversationContext.slots || {}) : {};
    const semanticSlots = semanticDecision?.slots || {};
    const mergedSlots = {
      ...existingSlots,
      ...semanticSlots
    };
    const prepared = prepareFreefireCalculatorSkill({
      slots: mergedSlots,
      requestId,
      language
    });

    if (prepared.status !== 'ready') {
      return finalizeSkillTurn(buildFreefireSkillTurn(prepared), semanticDecision);
    }

    const capabilityId = getFreefireCapabilityId();
    if (typeof onRunning === 'function') {
      onRunning({
        skillId: MI_SKILL_IDS.FREEFIRE_CALCULATOR,
        capabilityId,
        mountedAdapter: 'FreefireCalculatorSkillAdapter',
        message: 'Calculando Free Fire...'
      });
    }

    const outcome = await executeFreefireCalculatorSkill({
      slots: mergedSlots,
      fetchImpl,
      apiBaseUrl,
      authToken,
      getAuthToken: getAuthToken || (() => host.user?.getIdToken?.()),
      requestId,
      language
    });

    return finalizeSkillTurn(buildFreefireSkillTurn(outcome), semanticDecision);
  }

  const shouldRunMiAnalysisSkill = hasPendingMiAnalysisSkill || (
    canStartNewSkill && decisionTargetsSkill(semanticDecision, MI_SKILL_IDS.MI_ANALYSIS, false)
  );

  if (shouldRunMiAnalysisSkill) {
    const existingSlots = hasPendingMiAnalysisSkill ? (conversationContext.slots || {}) : {};
    const semanticSlots = { ...(semanticDecision?.slots || {}) };
    // O resolvedor LLM só vê um resumo do vídeo ({provided, name}) e pode
    // ecoá-lo como string/objeto — isso nunca substitui o File real da
    // conversa (senão a Mi pede o vídeo de novo logo após o upload).
    if (!isRealVideoFile(semanticSlots.video)) {
      delete semanticSlots.video;
    }
    const mergedSlots = {
      ...existingSlots,
      ...semanticSlots
    };
    const prepared = prepareMiAnalysisSkill({ slots: mergedSlots, host });

    if (prepared.status !== 'ready') {
      return finalizeSkillTurn(buildMiAnalysisSkillTurn(prepared), semanticDecision);
    }

    const capabilityId = getMiAnalysisCapabilityId();
    if (typeof onRunning === 'function') {
      onRunning({
        skillId: MI_SKILL_IDS.MI_ANALYSIS,
        capabilityId,
        mountedAdapter: 'MiAnalysisSkillAdapter',
        message: 'Iniciando Mi Analise...'
      });
    }

    const outcome = await executeMiAnalysisSkill({
      slots: mergedSlots,
      host,
      language
    });

    return finalizeSkillTurn(buildMiAnalysisSkillTurn(outcome), semanticDecision);
  }

  const shouldRunCoachSkill = hasPendingCoachSkill || (
    canStartNewSkill && decisionTargetsSkill(semanticDecision, MI_SKILL_IDS.COACH_TRAINING, false)
  );

  if (shouldRunCoachSkill) {
    const existingSlots = hasPendingCoachSkill ? (conversationContext.slots || {}) : {};
    const semanticSlots = semanticDecision?.slots || {};
    const mergedSlots = {
      ...existingSlots,
      ...semanticSlots
    };
    const prepared = prepareCoachTrainingSkill({ slots: mergedSlots });

    if (prepared.status !== 'ready') {
      return finalizeSkillTurn(buildCoachSkillTurn(prepared), semanticDecision);
    }

    const capabilityId = getCoachCapabilityId();
    if (typeof onRunning === 'function') {
      onRunning({
        skillId: MI_SKILL_IDS.COACH_TRAINING,
        capabilityId,
        mountedAdapter: 'CoachTrainingSkillAdapter',
        message: 'Buscando treino da Coach...'
      });
    }

    const outcome = await executeCoachTrainingSkill({
      slots: mergedSlots,
      fetchImpl,
      apiBaseUrl,
      authToken,
      getAuthToken: getAuthToken || (() => host.user?.getIdToken?.()),
      language
    });

    return finalizeSkillTurn(buildCoachSkillTurn(outcome), semanticDecision);
  }

  const shouldRunSensiSkill = hasPendingSensiSkill || (
    canStartNewSkill && decisionTargetsSkill(semanticDecision, MI_SKILL_IDS.SENSI_IA, false)
  );

  if (shouldRunSensiSkill) {
    const existingSlots = hasPendingSensiSkill ? (conversationContext.slots || {}) : {};
    const semanticSlots = semanticDecision?.slots || {};
    const mergedSlots = {
      ...existingSlots,
      ...semanticSlots
    };
    const prepared = prepareSensiIaSkill({
      slots: mergedSlots,
      mode: 'report_only',
      language
    });

    if (prepared.status !== 'ready') {
      return finalizeSkillTurn(buildSensiSkillTurn(prepared), semanticDecision);
    }

    const capabilityId = getSensiCapabilityId();
    if (typeof onRunning === 'function') {
      onRunning({
        skillId: MI_SKILL_IDS.SENSI_IA,
        capabilityId,
        mountedAdapter: 'SensiIaSkillAdapter',
        message: 'Diagnosticando sensibilidade...'
      });
    }

    const outcome = await executeSensiIaSkill({
      slots: mergedSlots,
      mode: 'report_only',
      fetchImpl,
      apiBaseUrl,
      authToken,
      getAuthToken: getAuthToken || (() => host.user?.getIdToken?.()),
      language
    });

    return finalizeSkillTurn(buildSensiSkillTurn(outcome), semanticDecision);
  }

  const shouldRunPubgSkill = hasPendingPubgSkill || (
    canStartNewSkill
    && (
      canUseFallback
      || decisionTargetsSkill(semanticDecision, MI_SKILL_IDS.PUBG_MOBILE_CALCULATOR, false)
    )
  );

  if (!shouldRunPubgSkill) {
    // Decisao skill-side que nao passou no gate de confianca: nunca vira
    // fallback generico — vira clarificacao util com os slots preservados.
    if (
      semanticSkillId
      && (SKILL_SIDE_INTENTS.has(semanticDecision.intent) || semanticDecision.intent === 'ask_clarification')
    ) {
      return buildClarificationTurn(semanticDecision);
    }
    return { handled: false, semanticDecision };
  }

  const existingSlots = conversationContext?.pendingSkill === MI_SKILL_IDS.PUBG_MOBILE_CALCULATOR
    ? (conversationContext.slots || {})
    : {};
  const semanticSlots = semanticDecision?.slots || {};
  const prepared = preparePubgMobileCalculatorSkill({
    text,
    slots: {
      ...existingSlots,
      ...semanticSlots
    }
  });

  if (prepared.status !== 'ready') {
    return finalizeSkillTurn(buildPubgSkillTurn(prepared), semanticDecision);
  }

  const capabilityId = getPubgCapabilityId();
  if (typeof onRunning === 'function') {
    onRunning({
      skillId: MI_SKILL_IDS.PUBG_MOBILE_CALCULATOR,
      capabilityId,
      mountedAdapter: 'PubgMobileCalculatorSkillAdapter',
      message: 'Calculando miras...'
    });
  }

  const outcome = await executePubgMobileCalculatorSkill({
    text,
    slots: {
      ...existingSlots,
      ...semanticSlots
    },
    fetchImpl,
    apiBaseUrl,
    authToken,
    getAuthToken: getAuthToken || (() => host.user?.getIdToken?.()),
    requestId,
    language
  });

  return finalizeSkillTurn(buildPubgSkillTurn(outcome), semanticDecision);
};

export default runMiSkillTurn;
